/* eslint-disable indent */
import { html, css, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import sharedStyles from '../../app/styles.js';
import { translate } from '../../helpers/translate.js';
import '@polymer/paper-input/paper-input.js';
import '@sdb-develop/learning-frontend';

/**
 * Key/value editor laid out as a small property grid: one row per parameter with an editable name
 * and value and a delete button. "Add" opens a draft row that becomes a parameter once it has a
 * name; a pasted "name=value" in the name column is split. Emits `changed` with the complete
 * parameters object (insertion order kept) on every edit; the `parameters` property itself is not
 * mutated.
 */
@customElement('sdb-parameters')
class SdbParameters extends LitElement {

    @property({ type: Object })
    accessor parameters = {};

    // draft row opened with "Add"; null when none is open
    @state()
    accessor draft = null;

    // name of the row whose last rename clashed with an existing name ('' = the draft row)
    @state()
    accessor invalidName = null;

    get entries() {
        return Object.entries(this.parameters || {});
    }

    static styles = [
        sharedStyles,
        css`
            .grid {
                display: grid;
                grid-template-columns: minmax(180px, 1fr) 2fr 40px;
                column-gap: 16px;
                align-items: center;
            }

            .header {
                font-size: 12px;
                color: #888;
                padding-bottom: 4px;
            }

            paper-input {
                width: 100%;
                margin: 0;
            }

            .actions {
                margin-top: 12px;
            }
        `,
    ];

    render() {
        const hasRows = this.entries.length > 0 || this.draft;

        // .grid sets display, which wins over the [hidden] default, so render conditionally
        return html`
            ${!hasRows ? '' : html`
            <div class="grid">
                <div class="header">${translate('parameterName')}</div>
                <div class="header">${translate('parameterValue')}</div>
                <div class="header"></div>

                ${repeat(this.entries, ([key]) => key, ([key, value]) => html`
                    <paper-input
                        class="name"
                        transparent
                        no-label-float
                        .value=${key}
                        ?invalid=${this.invalidName === key}
                        error-message=${translate('errDuplicateParameter')}
                        @change=${(e) => this._rename(key, e.target)}>
                    </paper-input>
                    <paper-input
                        class="value"
                        transparent
                        no-label-float
                        .value=${value ?? ''}
                        @change=${(e) => this._update(key, e.target.value)}>
                    </paper-input>
                    <sdb-content-iconbutton
                        icon="fal fa-trash-can"
                        title=${translate('delete')}
                        @click=${() => this._remove(key)}>
                    </sdb-content-iconbutton>
                `)}

                ${this.draft ? html`
                    <paper-input
                        id="draft_name_input"
                        class="name"
                        transparent
                        no-label-float
                        autofocus
                        placeholder=${translate('parameterName')}
                        .value=${this.draft.name}
                        ?invalid=${this.invalidName === ''}
                        error-message=${translate('errDuplicateParameter')}
                        @value-changed=${(e) => { this.draft = { ...this.draft, name: e.target.value ?? '' }; }}
                        @change=${() => this._commitDraft()}>
                    </paper-input>
                    <paper-input
                        id="draft_value_input"
                        class="value"
                        transparent
                        no-label-float
                        placeholder=${translate('parameterValue')}
                        .value=${this.draft.value}
                        @value-changed=${(e) => { this.draft = { ...this.draft, value: e.target.value ?? '' }; }}
                        @change=${() => this._commitDraft()}>
                    </paper-input>
                    <sdb-content-iconbutton
                        icon="fal fa-trash-can"
                        title=${translate('cancel')}
                        @click=${() => { this.draft = null; this.invalidName = null; }}>
                    </sdb-content-iconbutton>
                ` : ''}
            </div>
            `}

            <div class="actions">
                <sdb-content-button
                    id="add_parameter_button"
                    label=${translate('addParameter')}
                    outlined
                    ?disabled=${!!this.draft}
                    @click=${() => { this.draft = { name: '', value: '' }; }}>
                </sdb-content-button>
            </div>
        `;
    }

    /** Turns the draft row into a parameter as soon as it has a name; "name=value" in the name column is split. */
    _commitDraft() {
        if (!this.draft) {
            return;
        }

        let name = this.draft.name.trim();
        let value = this.draft.value;

        if (!name) {
            return;
        }

        const separator = name.indexOf('=');
        if (separator > 0 && !value) {
            value = name.substring(separator + 1).trim();
            name = name.substring(0, separator).trim();
        }

        if (name in (this.parameters || {})) {
            this.invalidName = '';
            return;
        }

        this.invalidName = null;
        this.draft = null;
        this._emit({ ...this.parameters, [name]: value });
    }

    _rename(oldKey, input) {
        const newKey = (input.value ?? '').trim();

        if (!newKey || newKey === oldKey) {
            input.value = oldKey;
            return;
        }

        if (newKey in this.parameters) {
            this.invalidName = oldKey;
            input.value = oldKey;
            return;
        }

        this.invalidName = null;
        this._emit(Object.fromEntries(this.entries.map(([key, value]) => [key === oldKey ? newKey : key, value])));
    }

    _update(key, value) {
        this._emit({ ...this.parameters, [key]: value });
    }

    _remove(key) {
        if (!(key in (this.parameters || {}))) {
            return;
        }

        const { [key]: _, ...rest } = this.parameters;
        this._emit(rest);
    }

    _emit(parameters) {
        this.parameters = parameters;
        this.dispatchEvent(
            new CustomEvent('changed', {
                bubbles: true,
                composed: true,
                detail: {
                    value: { ...parameters },
                },
            }),
        );
    }
}
